import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A small, self-contained custom element that renders a thin vertical
 * scroll-progress indicator for the desktop product media gallery.
 *
 * It is intentionally NOT a `@theme/component` subclass: it holds no refs and
 * must never interfere with the gallery's own ref/mutation machinery (the grid
 * `media[]` refs are reused by the zoom dialog's view transitions). It only
 * reads the gallery grid's geometry and writes two CSS custom properties:
 *   --gallery-scroll-progress (0..1)  — drives the thumb translateY in CSS
 *   --gallery-scroll-thumb-height (px) — dynamically scales the thumb height
 *
 * Thumb height = max(MIN_THUMB_PX, TRACK_HEIGHT_PX × viewportH / gridH),
 * so the thumb is proportional to how much of the gallery is on-screen,
 * with a minimum of 33 px (Figma spec). Both constants match the CSS
 * variables defined on .gallery-scroll-indicator in the stylesheet.
 *
 * Desktop only: the desktop gallery grid is page-scrolled (a tall column), so
 * progress is derived from the grid's bounding rect relative to the viewport.
 * On mobile the element does nothing (and is hidden via CSS).
 *
 * Progressive enhancement: with no JS the thumb stays at the top (progress 0)
 * at its CSS-default height, and the indicator is harmless; CSS hides it
 * whenever there's nothing to scroll.
 */
class GalleryScrollIndicator extends HTMLElement {
  /** Must match --gallery-scroll-track-height in the stylesheet. */
  static #TRACK_HEIGHT_PX = 125;
  /** Must match the initial --gallery-scroll-thumb-height in the stylesheet. */
  static #MIN_THUMB_PX = 33;
  /** @type {HTMLElement | null} */
  #grid = null;
  /** @type {number | null} */
  #rafId = null;
  #abortController = new AbortController();

  connectedCallback() {
    // The grid lives as a sibling inside the same <media-gallery>.
    const gallery = this.closest('media-gallery');
    this.#grid = gallery?.querySelector('.media-gallery__grid') ?? null;

    if (!this.#grid) {
      this.hidden = true;
      return;
    }

    const { signal } = this.#abortController;
    window.addEventListener('scroll', this.#requestUpdate, { passive: true, signal });
    window.addEventListener('resize', this.#requestUpdate, { passive: true, signal });

    // Initial paint (after layout settles).
    this.#requestUpdate();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #requestUpdate = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(this.#update);
  };

  #update = () => {
    this.#rafId = null;

    const grid = this.#grid;
    if (!grid) return;

    // Desktop only — the grid is hidden on mobile, so there's nothing to track.
    if (isMobileBreakpoint()) {
      this.hidden = true;
      return;
    }

    const rect = grid.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    // Show only while the indicator track sits within the gallery bounds:
    // - Entry: gallery top must be above the track's top edge (50vh - 62.5px)
    // - Exit:  gallery bottom must be below 75vh — hides at roughly the 3/4
    //          point of the last image so the indicator never lingers near the
    //          gallery/recommendations boundary.
    const indicatorTop = viewportHeight / 2 - GalleryScrollIndicator.#TRACK_HEIGHT_PX / 2;
    const exitThreshold = viewportHeight * 0.75;
    if (rect.top > indicatorTop || rect.bottom < exitThreshold) {
      this.hidden = true;
      return;
    }

    // Total distance the grid travels through the viewport, from "top of grid
    // at top of viewport" to "bottom of grid at bottom of viewport".
    const scrollableDistance = rect.height - viewportHeight;

    // Nothing meaningful to scroll (grid fits in the viewport): keep it hidden
    // so the indicator never shows a misleading full-travel thumb.
    if (scrollableDistance <= 1) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    // rect.top starts at >= 0 (grid below the fold) and goes negative as we
    // scroll past it. Progress 0 = grid top aligned with viewport top,
    // progress 1 = grid bottom aligned with viewport bottom.
    const scrolled = -rect.top;
    const progress = Math.min(1, Math.max(0, scrolled / scrollableDistance));

    // Thumb height is proportional to the visible fraction of the gallery,
    // clamped to a minimum of 33 px (Figma spec).
    const thumbHeight = Math.max(
      GalleryScrollIndicator.#MIN_THUMB_PX,
      Math.round(GalleryScrollIndicator.#TRACK_HEIGHT_PX * (viewportHeight / rect.height))
    );

    this.style.setProperty('--gallery-scroll-progress', `${progress}`);
    this.style.setProperty('--gallery-scroll-thumb-height', `${thumbHeight}px`);
  };
}

if (!customElements.get('gallery-scroll-indicator')) {
  customElements.define('gallery-scroll-indicator', GalleryScrollIndicator);
}
